import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { useMatrixParams } from '@/app/MatrixSwitch';
import type { ClientDetail, CompanyLookupResult, CompanySearchMatch } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { selectClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { setClientCommunityInterest } from '@/features/taxreturn/menus/menus';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { Accordion, ExpansionPanel } from '@/kit/ExpansionPanel';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Radio, RadioGroup } from '@/kit/Radio';
import { RequiredMark } from '@/kit/RequiredMark';
import { MatSelect } from '@/kit/MatSelect';
import { Select, SelectOption } from '@/kit/Select';
import { SideList } from '@/kit/SideList';
import { Tooltip } from '@/kit/Tooltip';
import { buttonLinkProps } from '../buttonLink';
import { validateAddresses, validateForm } from '../formRules';
import { ClientLogoField } from './ClientLogoField';
import {
  REGISTRATION_NUMBER_HINT,
  companyNameMaxFor,
  applyClientTypeRules,
  applyCompanyLookup,
  blankAddress,
  blankContact,
  bookStartDateError,
  buildClientPayload,
  clientValuesFrom,
  controlErrors,
  errorFor,
  isClientFormValid,
  newClientValues,
  removeRow,
  setPrimary,
  typeFlags,
  validateContacts,
  vatRegistrationDateError,
  withRegisteredOffice,
  CONTROL_NAMES,
  type ClientAddressLine,
  type ClientControlName,
  type ClientFormValues,
  type ContactLine,
} from './addClientRules';
import './AddClient.scss';

/** MY_DATE_FORMATS.display.dateInput */
const DATE_FORMAT = 'DD-MMM-YYYY';
/** Year End only ever shows day and month: the same date recurs every year, so a year on screen would be misleading. */
const YEAR_END_FORMAT = 'DD-MMM';

/** A `ClientFormValues` date field as a `Dayjs`, or null if unset/unparsable. */
const toDayjsOrNull = (value: Dayjs | string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Year End carries only a day and month; its year is derived, not chosen, because that stored date becomes the end
 * of the client's first accounting period (AccountingPeriodScreenService.GenerateAsync) - it must fall after the
 * client's start, not in the past. Anchored on Book Start Date, else Business Start Date, else today: the earliest
 * occurrence of the picked day/month that is still after the anchor.
 */
const yearEndFor = (picked: Dayjs, anchor: Dayjs | null): Dayjs => {
  const base = anchor ?? dayjs();
  const sameYear = picked.year(base.year());
  return sameYear.isAfter(base, 'day') ? sameYear : sameYear.add(1, 'year');
};

type TextControl = {
  [K in ClientControlName]: ClientFormValues[K] extends string | null ? K : never;
}[ClientControlName];

/** Angular `AddClientComponent`: create or edit a client (`/home/manage/addClient[;id=<base64 universalId>]`). */
export function AddClient() {
  const navigate = useNavigate();
  const { id: encodedId, from } = useMatrixParams();
  const isFromDashboard = from === 'dashboard';

  const [values, setValues] = useState<ClientFormValues>(() => newClientValues());
  const [dirty, setDirty] = useState<Set<ClientControlName>>(new Set());
  const [touched, setTouched] = useState<Set<ClientControlName>>(new Set());
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [isLookingUpCompany, setIsLookingUpCompany] = useState(false);
  /** True while a save is in flight, so a second click on Save / Save and Exit cannot submit twice. */
  const [saving, setSaving] = useState(false);

  // prod-p2: Companies House search by name, for users who do not know the registration number.
  const [companySearchText, setCompanySearchText] = useState('');
  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
  const [hasSearchedCompanies, setHasSearchedCompanies] = useState(false);
  const [companyMatches, setCompanyMatches] = useState<CompanySearchMatch[]>([]);

  const [dataSource, setDataSource] = useState<ClientAddressLine[]>(() => [blankAddress(true)]);
  const [dataContactSource, setDataContactSource] = useState<ContactLine[]>(() => [blankContact(true)]);

  const clientUniversalId = useRef<string | null>(null);
  /** The saved client's id as state, for the logo control (the ref above cannot be read while rendering). */
  const [savedClientId, setSavedClientId] = useState<string | null>(null);
  const mounted = useRef(false);

  const flags = typeFlags(values.clientType);

  const resetStates = () => {
    setDirty(new Set());
    setTouched(new Set());
  };

  const setValue = <K extends ClientControlName>(name: K, value: ClientFormValues[K]) => {
    setValues((current) => ({ ...current, [name]: value }));
    setDirty((current) => new Set(current).add(name));
  };

  const patchValue = (patch: Partial<ClientFormValues>) => setValues((current) => ({ ...current, ...patch }));

  const touch = (name: ClientControlName) => setTouched((current) => new Set(current).add(name));

  const messageFor = (name: ClientControlName) => errorFor(name, values, dirty.has(name) || touched.has(name));

  /** Angular `ng-invalid ng-touched` on the control. */
  const isInvalid = (name: ClientControlName) => controlErrors(name, values) !== null && touched.has(name);

  const textProps = (name: TextControl) => ({
    value: values[name] ?? '',
    onChange: (event: ChangeEvent<HTMLInputElement>) => setValue(name, event.target.value),
    onBlur: () => touch(name),
    invalid: isInvalid(name),
  });

  const selectProps = (name: 'companyType' | 'vatScheme' | 'vatSubmitType') => ({
    value: values[name],
    onChange: (value: unknown) => setValue(name, value as string | null),
    onBlur: () => touch(name),
    className: `w-300${isInvalid(name) ? ' ng-invalid ng-touched' : ''}`,
  });

  const dateProps = (name: 'vatRegistrationDate') => ({
    value: values[name],
    onChange: (value: Dayjs | null) => setValue(name, value),
  });

  /** The calendar only ever reports a day+month pick for Year End; give it a real year before it reaches the form. */
  const onYearEndChange = (picked: Dayjs | null) => {
    if (!picked) {
      setValue('yearEnd', null);
      return;
    }
    const anchor = toDayjsOrNull(values.bookStartDate) ?? toDayjsOrNull(values.businessStartDate);
    setValue('yearEnd', yearEndFor(picked, anchor));
  };

  /**
   * Year End may be picked before Book Start Date / Business Start Date are filled in, anchored on today for lack of
   * anything better; once either is set (or changes), its already-picked day/month is re-anchored so the derived year
   * still lands right after the client's actual start, not today's.
   */
  const rederiveYearEnd = (book: Dayjs | string | null, business: Dayjs | string | null) => {
    const picked = toDayjsOrNull(values.yearEnd);
    if (!picked) return;
    const anchor = toDayjsOrNull(book) ?? toDayjsOrNull(business);
    setValue('yearEnd', yearEndFor(picked, anchor));
  };

  const onBusinessStartDateChange = (value: Dayjs | null) => {
    setValue('businessStartDate', value);
    rederiveYearEnd(values.bookStartDate, value);
  };

  const onBookStartDateChange = (value: Dayjs | null) => {
    setValue('bookStartDate', value);
    rederiveYearEnd(value, values.businessStartDate);
  };

  const populate = (client: ClientDetail) => {
    if (!mounted.current) return;
    setIsEdit(true);
    clientUniversalId.current = client.id;
    setSavedClientId(client.id);
    setValues(clientValuesFrom(client));
    resetStates();

    setDataSource(
      client.addresses.length > 0
        ? client.addresses.map((a) => ({
            addressType: String(a.addressType),
            addressLine: a.addressLine,
            city: a.city,
            county: a.county ?? '',
            country: a.country,
            postCode: a.postCode,
            isPrimary: a.isPrimary,
          }))
        : [blankAddress(true)],
    );

    setDataContactSource(
      client.contacts.length > 0
        ? client.contacts.map((c) => ({
            contactId: c.contactId,
            title: c.title ?? '',
            fullName: c.fullName,
            email: c.email ?? '',
            phone: c.phone ?? '',
            isPrimary: c.isPrimary,
          }))
        : [blankContact(true)],
    );
  };

  const load = (id: string) => {
    practiceService.getClient(id).then(populate, (error) => notifyError(error));
  };

  const startNew = () => {
    setIsEdit(false);
    clientUniversalId.current = null;
    setSavedClientId(null);
    setValues(newClientValues());
    resetStates();
    setDataSource([blankAddress(true)]);
    setDataContactSource([blankContact(true)]);

    practiceService.getNextClientId().then(
      (response) => {
        if (mounted.current) patchValue({ clientId: response.clientId });
      },
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The side list navigates between clients on this same route, so react to every change of the id.
  useEffect(() => {
    if (encodedId) {
      load(atob(encodedId));
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the route param (paramMap subscription) drives the form
      startNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load/startNew only use refs and stable setters
  }, [encodedId]);

  const toggleAccordion = () => setIsExpandAll((current) => !current);

  const onSelectionChange = (value: string) => {
    setValues((current) => applyClientTypeRules(current, value));
    setDirty((current) => new Set(current).add('clientType'));
  };

  /**
   * APT241: takes everything Companies House holds for the chosen company into the form (not only the name): the
   * client and company type, CIC, business/book start dates, year end and the registered office address.
   */
  const applyCompany = (company: CompanyLookupResult, typedNumber: string) => {
    setValues((current) => applyCompanyLookup(current, company, typedNumber));
    setDataSource((rows) => withRegisteredOffice(rows, company.registeredOffice));
  };

  /** ap5-a3: fills the form from Companies House for the registration number entered (APT241: every field). */
  const onCompaniesHouseLookup = () => {
    const number = (values.registrationNumber ?? '').toString().trim();
    if (!number) {
      notifyError('Enter the registration number first, then look it up.');
      return;
    }

    setIsLookingUpCompany(true);
    practiceService
      .lookupCompany(number)
      .then(
        (company) => {
          if (!company?.companyName) {
            notifyError('No company was found at Companies House for that registration number.');
            return;
          }
          applyCompany(company, number);
          notifySuccess(`Found ${company.companyName} at Companies House.`);
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        if (mounted.current) setIsLookingUpCompany(false);
      });
  };

  /** prod-p2: searches Companies House by company name and offers the matches to choose from. */
  const onCompaniesHouseSearch = () => {
    const name = (companySearchText ?? '').trim();
    if (name.length < 2) {
      notifyError('Enter at least two characters of the company name.');
      return;
    }

    setIsSearchingCompanies(true);
    practiceService
      .searchCompany(name)
      .then(
        (response) => {
          setCompanyMatches(response?.companies ?? []);
          setHasSearchedCompanies(true);
        },
        (error) => {
          setCompanyMatches([]);
          setHasSearchedCompanies(true);
          notifyError(error);
        },
      )
      .finally(() => {
        if (mounted.current) setIsSearchingCompanies(false);
      });
  };

  /**
   * prod-p2: takes the chosen search result into the form and clears the picker.
   * Only the name and number come from a search hit — unlike the explicit "look up by registration
   * number" action, this must not pull in and overwrite the rest of the form (business/book start
   * dates, year end, company type) with the full Companies House profile.
   */
  const selectCompanyMatch = (match: CompanySearchMatch) => {
    patchValue({
      companyName: match.companyName,
      registrationNumber: match.companyNumber,
    });
    setCompanyMatches([]);
    setHasSearchedCompanies(false);
    setCompanySearchText('');
    notifySuccess(`Selected ${match.companyName} (${match.companyNumber}).`);
  };

  const clearCompanySearch = () => {
    setCompanyMatches([]);
    setHasSearchedCompanies(false);
    setCompanySearchText('');
  };

  const updateAddress = (index: number, patch: Partial<ClientAddressLine>) =>
    setDataSource((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const updateContact = (index: number, patch: Partial<ContactLine>) =>
    setDataContactSource((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addAddress = () => setDataSource((rows) => [...rows, blankAddress(rows.length === 0)]);
  const removeAddress = (index: number) => setDataSource((rows) => removeRow(rows, index, () => blankAddress(true)));
  const setPrimaryAddress = (index: number) => setDataSource((rows) => setPrimary(rows, index));

  const addContact = () => setDataContactSource((rows) => [...rows, blankContact(rows.length === 0)]);
  const removeContact = (index: number) =>
    setDataContactSource((rows) => removeRow(rows, index, () => blankContact(true)));
  const setPrimaryContact = (index: number) => setDataContactSource((rows) => setPrimary(rows, index));

  const onCancel = () => {
    if (isEdit && clientUniversalId.current) {
      load(clientUniversalId.current);
    } else {
      startNew();
    }
  };

  const onClose = () => void navigate(isFromDashboard ? '/home/taxreturns/dashboard' : '/home/manage/clients');

  const save = (exitAfterSave: boolean) => {
    if (!validateForm(isClientFormValid(values))) {
      setTouched(new Set(CONTROL_NAMES));
      return;
    }

    // task-47: the address rows are ngModel-bound, so like the contacts they are never seen
    // by validateForm and have to be checked here.
    // APT364: Address is marked mandatory, so a new client needs one (an older client without an address can still be edited and saved).
    const addressError = validateAddresses(dataSource, !isEdit);
    if (addressError) {
      notifyError(addressError);
      return;
    }

    // ap5-a3: the contact rows are ngModel-bound, so they are checked here rather than by the form.
    const contactError = validateContacts(dataContactSource);
    if (contactError) {
      notifyError(contactError);
      return;
    }

    const payload = buildClientPayload(clientUniversalId.current, values, dataSource, dataContactSource);

    setSaving(true);
    practiceService.saveClient(payload).then(
      (response) => {
        setClientCommunityInterest(response.universalId, Boolean(values.isCommunityInterest));
        notifySuccess(response.clientMessage);
        if (exitAfterSave || isFromDashboard) {
          selectClient(response.universalId, values.companyName || values.clientId || 'Client');
          void navigate(isFromDashboard ? '/home/taxreturns/dashboard' : '/home/manage/clients');
        } else if (!isEdit) {
          void navigate(matrixPath('/home/manage', 'addClient', { id: btoa(response.universalId) }));
        } else {
          load(response.universalId);
        }
      },
      (error) => notifyError(error),
    ).finally(() => {
      if (mounted.current) setSaving(false);
    });
  };

  const companyMatchColumnDefs: DataTableColumn<CompanySearchMatch>[] = [
    {
      id: 'companyName',
      header: 'Company Name',
      cell: (match) => (
        <>
          <a className="link" {...buttonLinkProps(() => selectCompanyMatch(match))}>
            {match.companyName}
          </a>
          {match.address && <div className="font-size-12">{match.address}</div>}
        </>
      ),
    },
    { id: 'companyNumber', header: 'Number', cell: (match) => match.companyNumber },
    { id: 'companyStatus', header: 'Status', cell: (match) => match.companyStatus || '-' },
    {
      id: 'choose',
      header: '',
      cell: (match) => (
        <Button className="action-button secondary-button" onClick={() => selectCompanyMatch(match)}>
          Use this
        </Button>
      ),
    },
  ];

  const addressColumns: DataTableColumn<ClientAddressLine>[] = [
    {
      id: 'name',
      header: '',
      headerClassName: 'w-100',
      cell: (element, index) => (
        <MatSelect
          className="w-100"
          placeholder="Postal"
          panelClass="myPanelClass"
          value={element.addressType}
          options={[
            { value: '0', label: 'Postal' },
            { value: '1', label: 'Perso.' },
          ]}
          onChange={(value) => updateAddress(index, { addressType: value })}
        />
      ),
    },
    {
      id: 'address',
      header: (
        <>
          Address<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'wp-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="wp-100"
          maxLength={500}
          value={element.addressLine}
          onChange={(event) => updateAddress(index, { addressLine: event.target.value })}
        />
      ),
    },
    {
      id: 'city',
      header: (
        <>
          City/Town<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={100}
          charMode="text"
          value={element.city}
          onChange={(event) => updateAddress(index, { city: event.target.value })}
        />
      ),
    },
    {
      id: 'county',
      header: 'County',
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={100}
          charMode="text"
          value={element.county}
          onChange={(event) => updateAddress(index, { county: event.target.value })}
        />
      ),
    },
    {
      id: 'country',
      header: (
        <>
          Country<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'w-120',
      cell: (element) => <InputField fieldClassName="w-120" maxLength={100} disabled value={element.country} />,
    },
    {
      id: 'zip',
      header: (
        <>
          Post Code<span className="text-red">*</span>
        </>
      ),
      headerClassName: 'w-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-100"
          maxLength={20}
          charMode="alphanumeric"
          value={element.postCode}
          onChange={(event) => updateAddress(index, { postCode: event.target.value })}
        />
      ),
    },
    {
      id: 'primary',
      header: 'Is Primary',
      headerClassName: 'w-100 text-align-center',
      cellClassName: 'text-align-center',
      cell: (element, index) => (
        <RadioGroup value={element.isPrimary ? '0' : null} onChange={() => setPrimaryAddress(index)}>
          <Radio value="0" hostClassName="example-radio-button" />
        </RadioGroup>
      ),
    },
    {
      id: 'close',
      header: '',
      headerClassName: 'w-50',
      cell: (_element, index) => (
        <Button className="close-icn" title="Delete" onClick={() => removeAddress(index)}>
          <Icon name="close" outlined />
        </Button>
      ),
    },
  ];

  const contactColumns: DataTableColumn<ContactLine>[] = [
    {
      id: 'fullname',
      header: 'Full Name',
      headerClassName: 'w-100',
      cell: (element, index) => (
        <div className="d-flex gap-15">
          <MatSelect
            className="w-100"
            placeholder="Mr"
            panelClass="myPanelClass"
            value={element.title}
            options={['Mr', 'Mrs', 'Miss', 'Ms'].map((title) => ({ value: title, label: title }))}
            onChange={(value) => updateContact(index, { title: value })}
          />
          <InputField
            fieldClassName="w-150"
            maxLength={255}
            charMode="text"
            value={element.fullName}
            onChange={(event) => updateContact(index, { fullName: event.target.value })}
          />
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      headerClassName: 'wp-100',
      cell: (element, index) => (
        <InputField
          fieldClassName="wp-100"
          type="email"
          maxLength={255}
          placeholder="name@example.co.uk"
          value={element.email}
          onChange={(event) => updateContact(index, { email: event.target.value })}
        />
      ),
    },
    {
      id: 'phone',
      header: 'Phone',
      headerClassName: 'w-150',
      cell: (element, index) => (
        <InputField
          fieldClassName="w-150"
          maxLength={20}
          placeholder="e.g. 020 7946 0000"
          charMode="number"
          extraChars="+()- "
          value={element.phone}
          onChange={(event) => updateContact(index, { phone: event.target.value })}
        />
      ),
    },
    {
      id: 'isprimary',
      header: 'Is Primary',
      headerClassName: 'w-100 text-align-center',
      cellClassName: 'text-align-center',
      cell: (element, index) => (
        <RadioGroup value={element.isPrimary ? '0' : null} onChange={() => setPrimaryContact(index)}>
          <Radio value="0" hostClassName="example-radio-button" />
        </RadioGroup>
      ),
    },
    {
      id: 'xclose',
      header: '',
      headerClassName: 'w-50',
      cell: (_element, index) => (
        <Button className="close-icn" title="Delete" onClick={() => removeContact(index)}>
          <Icon name="close" outlined />
        </Button>
      ),
    },
  ];

  const bookError = bookStartDateError(values);
  const vatDateError = vatRegistrationDateError(values);

  return (
    <div className="sidemenu-hide add-client-page">
      <div className="d-flex h-100">
        <SideList />
        <div className="form wp-100">
          <div>
            <div className="sidenav-details mb-4">
              {/* Design review 2 Nov 2026: Save / Save and Exit / Cancel Changes are page actions, so they sit top-right in the sticky
                  header; the footer bar is gone. */}
              <PageHeader title={isEdit ? 'Edit Client' : 'Add New Client'}>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(false)}>
                  Save
                </Button>
                <Button className="action-button primary-button" busy={saving} onClick={() => save(true)}>
                  Save and Exit
                </Button>
                {(savedClientId || isEdit) && (
                  <Button
                    className="action-button secondary-button"
                    title="Open"
                    aria-label="Open"
                    data-testid="open-client-btn"
                    onClick={() => {
                      const id = clientUniversalId.current || savedClientId;
                      if (id) {
                        selectClient(id, values.companyName || values.clientId || 'Client');
                        void navigate('/home/taxreturns/dashboard');
                      }
                    }}
                  >
                    Open
                  </Button>
                )}
                <Button className="action-button secondary-button" onClick={onCancel}>
                  Cancel Changes
                </Button>
                <Button className="close-btn" aria-label="Close" onClick={onClose}>
                  <Icon name="close" />
                </Button>
              </PageHeader>
              <div className="mt-1">
                <div className="accordion-toggle">
                  {isExpandAll && (
                    <Icon
                      name="keyboard_double_arrow_down"
                      onClick={() => {
                        toggleAccordion();
                        setPanelOpen(true);
                      }}
                    />
                  )}
                  {!isExpandAll && (
                    <Icon
                      name="keyboard_double_arrow_up"
                      onClick={() => {
                        toggleAccordion();
                        setPanelOpen(false);
                      }}
                    />
                  )}
                </div>
                <Accordion>
                  <ExpansionPanel title="Basic Info" expanded={panelOpen} onExpandedChange={setPanelOpen}>
                    <div className="sidenav-body p-1">
                      <form onSubmit={(event) => event.preventDefault()}>
                        <div className="sidenav-body">
                          <div className="sidenav-body-content p-0">
                            <div className="wp-100">
                              <div>
                                <div className="form-field">
                                  <p>
                                    Client ID<span className="text-red">*</span>
                                  </p>
                                  <InputField
                                    fieldClassName="w-300"
                                    maxLength={255}
                                    charMode="alphanumeric"
                                    {...textProps('clientId')}
                                  />
                                  {messageFor('clientId') && (
                                    <span className="text-red font-size-12">{messageFor('clientId')}</span>
                                  )}
                                </div>
                                <div className="form-field toggle-option mt-1 mb-0">
                                  <p>Client Type</p>
                                  <RadioGroup
                                    name="clientType"
                                    value={values.clientType}
                                    disabled={isEdit}
                                    onChange={(value) => onSelectionChange(value as string)}
                                  >
                                    <div className="d-flex align-center gap-15">
                                      <Radio value="0" hostClassName="example-radio-button">
                                        Limited Company
                                      </Radio>
                                      <Radio value="1" hostClassName="example-radio-button">
                                        Sole Trader
                                      </Radio>
                                      <Radio value="2" hostClassName="example-radio-button">
                                        Partnership
                                      </Radio>
                                      <Radio value="3" hostClassName="example-radio-button">
                                        LLP
                                      </Radio>
                                    </div>
                                  </RadioGroup>
                                </div>

                                <div className="hr-top pt-1 mt-5">
                                  <div className="d-flex gap-15">
                                    <div className="form-field">
                                      <p>
                                        {flags.companyNameLabel}
                                        <span className="text-red">*</span>
                                      </p>
                                      <InputField
                                        fieldClassName="w-300"
                                        maxLength={companyNameMaxFor(values.clientType)}
                                        name="companyName"
                                        id="companyName"
                                        aria-label={flags.companyNameLabel}
                                        data-testid="companyName"
                                        {...textProps('companyName')}
                                      />
                                      {messageFor('companyName') && (
                                        <span className="text-red font-size-12">{messageFor('companyName')}</span>
                                      )}
                                    </div>
                                    {flags.showCompanyFields && (
                                      <div className="form-field">
                                        <p>Type of Company</p>
                                        <Select {...selectProps('companyType')}>
                                          <SelectOption value="">Select Type</SelectOption>
                                          <SelectOption value="shares">Limited by Shares</SelectOption>
                                          <SelectOption value="guarantee">Limited by Guarantee</SelectOption>
                                        </Select>
                                      </div>
                                    )}
                                  </div>
                                  {flags.isTrader && (
                                    <div className="form-field">
                                      <p>
                                        National Insurance Number
                                        {values.clientType === '1' && <RequiredMark />}
                                      </p>
                                      <InputField
                                        fieldClassName="w-300"
                                        placeholder="e.g. AA999999A"
                                        maxLength={9}
                                        charMode="alphanumeric"
                                        {...textProps('nationalInsuranceNumber')}
                                      />
                                      {messageFor('nationalInsuranceNumber') && (
                                        <span className="text-red font-size-12">
                                          {messageFor('nationalInsuranceNumber')}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {flags.showCompanyFields && (
                                    <Checkbox
                                      checked={values.isCommunityInterest}
                                      onChange={(event) => setValue('isCommunityInterest', event.target.checked)}
                                    >
                                      Company is a Community Interest Company
                                    </Checkbox>
                                  )}
                                </div>

                                <div className="hr-top form-field-title mt-1 mb-1 pt-1">
                                  <p>Business Details</p>
                                </div>

                                {/* QA rows 218/221: the client's own logo, once the client exists. */}
                                {isEdit && savedClientId && (
                                  <ClientLogoField key={savedClientId} clientId={savedClientId} />
                                )}

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>Business Start Date</p>
                                    <DateField
                                      fieldClassName="w-300"
                                      format={DATE_FORMAT}
                                      readOnly
                                      max={dayjs()}
                                      value={values.businessStartDate}
                                      onChange={onBusinessStartDateChange}
                                    />
                                  </div>
                                  <div className="form-field">
                                    <p>Book Start Date</p>
                                    <DateField
                                      fieldClassName="w-300"
                                      format={DATE_FORMAT}
                                      readOnly
                                      value={values.bookStartDate}
                                      onChange={onBookStartDateChange}
                                    />
                                    {bookError && <span className="text-red font-size-12">{bookError}</span>}
                                  </div>
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>Year End</p>
                                    <DateField
                                      fieldClassName="w-300"
                                      format={YEAR_END_FORMAT}
                                      readOnly
                                      yearless
                                      value={values.yearEnd}
                                      onChange={onYearEndChange}
                                    />
                                  </div>
                                  {flags.showRegistrationNumber && (
                                    <div className="form-field">
                                      <p className="d-flex align-items-center gap-5">
                                        Registration Number{' '}
                                        <Tooltip title={REGISTRATION_NUMBER_HINT} placement="right">
                                          <Icon name="info" outlined className="font-size-16 text-primary" />
                                        </Tooltip>
                                      </p>
                                      <div className="d-flex align-items-center gap-5">
                                        <InputField
                                          fieldClassName="w-300"
                                          placeholder="e.g. 01234567 or SC123456"
                                          // BUG-020: 9 characters can be typed, as in 3.0; the full value is kept
                                          // and a number that is not Companies House's 8-character format says so.
                                          maxLength={9}
                                          charMode="alphanumeric"
                                          {...textProps('registrationNumber')}
                                        />
                                        <Button
                                          className="action-button secondary-button"
                                          title="Look up this registration number at Companies House"
                                          disabled={isLookingUpCompany}
                                          onClick={onCompaniesHouseLookup}
                                        >
                                          {isLookingUpCompany ? 'Looking up...' : 'Find at Companies House'}
                                        </Button>
                                      </div>
                                      {messageFor('registrationNumber') && (
                                        <span className="text-red font-size-12">
                                          {messageFor('registrationNumber')}
                                        </span>
                                      )}

                                      {/* prod-p2: search Companies House by name, for users who do not know the number. */}
                                      <p className="mt-1 mb-0">Don&apos;t know the number? Search by company name</p>
                                      <div className="d-flex align-items-center gap-5">
                                        <InputField
                                          fieldClassName="w-300"
                                          placeholder="e.g. Tesco"
                                          maxLength={160}
                                          value={companySearchText}
                                          onChange={(event) => setCompanySearchText(event.target.value)}
                                          onKeyUp={(event) => {
                                            if (event.key === 'Enter') onCompaniesHouseSearch();
                                          }}
                                        />
                                        <Button
                                          className="action-button secondary-button"
                                          title="Search Companies House for this company name"
                                          disabled={isSearchingCompanies}
                                          onClick={onCompaniesHouseSearch}
                                        >
                                          {isSearchingCompanies ? 'Searching...' : 'Search by name'}
                                        </Button>
                                        {hasSearchedCompanies && (
                                          <Button
                                            className="action-button secondary-button"
                                            onClick={clearCompanySearch}
                                          >
                                            Clear
                                          </Button>
                                        )}
                                      </div>

                                      {companyMatches.length > 0 && (
                                        <div className="mat-table-wrapper">
                                          <DataTable
                                            columns={companyMatchColumnDefs}
                                            rows={companyMatches}
                                            rowKey={(match, index) => `${match.companyNumber}-${index}`}
                                          />
                                        </div>
                                      )}
                                      {hasSearchedCompanies && companyMatches.length === 0 && (
                                        <span className="font-size-12">
                                          No companies at Companies House matched that name.
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>UTR No.</p>
                                    <InputField
                                      fieldClassName="w-300"
                                      placeholder="Should be 10 Digit"
                                      maxLength={10}
                                      charMode="number"
                                      {...textProps('utrNo')}
                                    />
                                    {messageFor('utrNo') && (
                                      <span className="text-red font-size-12">{messageFor('utrNo')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>VAT Scheme</p>
                                    <Select {...selectProps('vatScheme')}>
                                      <SelectOption value="">Select</SelectOption>
                                      <SelectOption value="standard">Standard (Accrual)</SelectOption>
                                      <SelectOption value="cash">Cash Accounting</SelectOption>
                                      <SelectOption value="flatrate">Flat Rate</SelectOption>
                                      <SelectOption value="flatratecash">Flat Rate (Cash Based)</SelectOption>
                                      <SelectOption value="annual">Annual Accounting</SelectOption>
                                      <SelectOption value="retail">Retail Scheme</SelectOption>
                                      <SelectOption value="margin">Margin Scheme</SelectOption>
                                      <SelectOption value="notregistered">Not VAT Registered</SelectOption>
                                    </Select>
                                  </div>
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>VAT Reg. No.</p>
                                    <InputField
                                      fieldClassName="w-300"
                                      placeholder="9 digits, e.g. 123456789"
                                      maxLength={9}
                                      charMode="number"
                                      {...textProps('vatRegNo')}
                                    />
                                    {messageFor('vatRegNo') && (
                                      <span className="text-red font-size-12">{messageFor('vatRegNo')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>VAT Registration Date</p>
                                    <DateField
                                      fieldClassName="w-300"
                                      format={DATE_FORMAT}
                                      readOnly
                                      max={dayjs()}
                                      {...dateProps('vatRegistrationDate')}
                                    />
                                    {vatDateError && <span className="text-red font-size-12">{vatDateError}</span>}
                                  </div>
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>VAT Submit Type</p>
                                    <Select {...selectProps('vatSubmitType')}>
                                      <SelectOption value="">Select</SelectOption>
                                      <SelectOption value="monthly">Monthly</SelectOption>
                                      <SelectOption value="quarterly">Quarterly</SelectOption>
                                      <SelectOption value="annually">Annually</SelectOption>
                                    </Select>
                                  </div>
                                </div>

                                <div className="d-flex gap-30">
                                  <div className="form-field">
                                    <p>PAYE Ref No.</p>
                                    <InputField
                                      fieldClassName="w-300"
                                      placeholder="e.g. 123/AB12345"
                                      maxLength={14}
                                      charMode="alphanumeric"
                                      extraChars="/"
                                      {...textProps('payeRefNo')}
                                    />
                                    {messageFor('payeRefNo') && (
                                      <span className="text-red font-size-12">{messageFor('payeRefNo')}</span>
                                    )}
                                  </div>
                                  <div className="form-field">
                                    <p>Accounts Office Ref No.</p>
                                    <InputField
                                      fieldClassName="w-300"
                                      placeholder="e.g. 123PA00123456"
                                      maxLength={13}
                                      charMode="alphanumeric"
                                      {...textProps('accountsOfficeRefNo')}
                                    />
                                    {messageFor('accountsOfficeRefNo') && (
                                      <span className="text-red font-size-12">{messageFor('accountsOfficeRefNo')}</span>
                                    )}
                                  </div>
                                </div>

                                <div className="content-box hr-top">
                                  <div className="form-field-title mt-1 mb-1">
                                    <p>Address Details</p>
                                  </div>
                                  <div className="sidenav-table sidenav-table-input wp-100">
                                    <div className="mat-table-wrapper contact-detail-table">
                                      <DataTable
                                        columns={addressColumns}
                                        rows={dataSource}
                                        rowKey={(_row, index) => index}
                                        stickyHeader
                                      />
                                    </div>
                                  </div>
                                  {/* ap5-a3: a client has one company address, so only offer a line when there is none. */}
                                  {dataSource.length === 0 && (
                                    <div className="mb-10 mt-10">
                                      <a
                                        href="#"
                                        className="link hyperlink"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          addAddress();
                                        }}
                                      >
                                        Add New Line
                                      </a>
                                    </div>
                                  )}
                                </div>

                                <div className="content-box hr-top mt-1">
                                  <div className="form-field-title mt-1 mb-1">
                                    <p>Contact Details</p>
                                  </div>
                                  <div className="sidenav-table sidenav-table-input wp-100">
                                    <div className="mat-table-wrapper contact-detail-table">
                                      <DataTable
                                        columns={contactColumns}
                                        rows={dataContactSource}
                                        rowKey={(_row, index) => index}
                                        stickyHeader
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-10 mt-10">
                                    <a
                                      href="#"
                                      className="link hyperlink"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        addContact();
                                      }}
                                    >
                                      Add New Line
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </ExpansionPanel>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
